import React, { ReactNode } from "react";

import { ExpressionConfiguration } from "../../rule-engine-core/Expressions/ExpressionConfiguration";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";
import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { ExpressionView, ExpressionViewEmptyBehavior } from "../../Expressions/ExpressionView";
import { Box, SxProps } from "@mui/material";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { ObserveState } from "../StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { ExpressionValueType } from "../../rule-engine-core/Expressions/ExpressionValueType";
import { getPropertyEditorValidationIssues, PropertyEditorValidationTooltip } from "./PropertyEditorValidationTooltip";

export interface PropertyEditorExpressionInterface {
    editObject: object;
    propertyName: string;
    label?: string;
    hint?: string;
    error?: string;
    resultType?: IDataDefinition;
    inputDataFunction?: () => DataDefinition;
    emptyBehavior?: ExpressionViewEmptyBehavior;
    onChange?: () => void;
    sx?: SxProps;
    ignoreDesignerValidation?: boolean;
    renderValueEditor?: (expression: ExpressionConfiguration) => ReactNode;
}

export const PropertyEditorExpression: React.FC<PropertyEditorExpressionInterface> = (
    props: PropertyEditorExpressionInterface,
) => {
    return (
        <ObserveState
            listenTo={props.editObject}
            properties={[props.propertyName]}
            control={() => {
                let expression =
                    (Reflect.get(props.editObject, props.propertyName) as ExpressionConfiguration) ??
                    new ExpressionConfiguration();
                if (props.resultType) {
                    expression.executionResult = () => props.resultType!;
                }
                if (props.inputDataFunction) {
                    expression.inputData = props.inputDataFunction;
                }

                let showBorder = true;

                if (
                    props.emptyBehavior === ExpressionViewEmptyBehavior.VALUE &&
                    (expression.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET ||
                        expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VALUE)
                ) {
                    showBorder = false;
                }

                if (
                    props.emptyBehavior === ExpressionViewEmptyBehavior.VARIABLE &&
                    (expression.rootExpression.expressionRoot.valueType === ExpressionValueType.UNSET ||
                        expression.rootExpression.expressionRoot.valueType === ExpressionValueType.VARIABLE)
                ) {
                    showBorder = false;
                }

                let validationIssues = getPropertyEditorValidationIssues(
                    props.editObject,
                    props.propertyName,
                    props.error,
                );

                if (validationIssues) showBorder = true;

                return (
                    <>
                        <PropertyEditorValidationTooltip
                            editObject={props.editObject}
                            propertyName={props.propertyName}
                            error={validationIssues}
                        >
                            <UILabel paddingBottom={showBorder ? "5px" : undefined}>{props.label}</UILabel>
                            <Box
                                sx={props.sx}
                                border={
                                    showBorder
                                        ? "1px solid " +
                                          (validationIssues
                                              ? UIStyleConstants.RED_COLOR
                                              : UIStyleConstants.GRAY_VERY_LIGHT_COLOR)
                                        : undefined
                                }
                                borderRadius="3pxpx"
                            >
                                <ExpressionView
                                    emptyBehavior={props.emptyBehavior || ExpressionViewEmptyBehavior.EXPRESSION}
                                    expression={expression}
                                    renderValueEditor={props.renderValueEditor}
                                    onUpdate={() => {
                                        Reflect.set(props.editObject, props.propertyName, expression);
                                        StateManager.propertyChanged(props.editObject, props.propertyName);
                                        if (props.onChange) props.onChange();
                                    }}
                                />
                            </Box>
                            <UILabel
                                color={validationIssues ? UIStyleConstants.RED_COLOR : undefined}
                                variant={"hint"}
                            >
                                {validationIssues?.message ?? props.hint}
                            </UILabel>
                        </PropertyEditorValidationTooltip>
                    </>
                );
            }}
        />
    );
};
